import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Loader2, Crown, LogIn } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PRIZE_COLORS = ["#EF4444", "#2563EB", "#10B981", "#F97316", "#D946EF", "#06B6D4", "#8B5CF6", "#F43F5E"];
const TRY_COLOR = "#FFF7D6";
const WEEKLY_COLOR = "#FACC15";
const JACKPOT_COLOR = "#FFD700";
const DARK_SEG = (s) => s.amount === 0 || s.tier === "jackpot" || s.tier === "weekly";

const segColor = (s, i) => (s.tier === "jackpot" ? JACKPOT_COLOR : s.tier === "weekly" ? WEEKLY_COLOR : s.amount === 0 ? TRY_COLOR : PRIZE_COLORS[i % PRIZE_COLORS.length]);

export default function SpinWheel() {
  const { user, refreshWallet } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const ta = lang === "ta";
  const guest = !user;
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);

  const load = () => {
    if (!user) { api.get("/spin/segments").then((r) => setStatus({ ...r.data, available: false, guest: true })).catch(() => {}); return; }
    api.get("/spin/status").then((r) => setStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [user]); // eslint-disable-line
  const [idle, setIdle] = useState(true);  // light, slow idle turning for everyone; a real spin takes over fast

  if (!status) return null;

  const segments = status.segments || [];
  const n = segments.length || 16;
  const seg = 360 / n;
  const bg = `conic-gradient(${segments.map((s, i) => `${segColor(s, i)} ${i * seg}deg ${(i + 1) * seg}deg`).join(",")})`;
  const fmtLeft = () => `${Math.floor((status.seconds_left || 0) / 3600)}h ${String(Math.floor(((status.seconds_left || 0) % 3600) / 60)).padStart(2, "0")}m`;

  const spin = async () => {
    if (guest) { navigate("/auth"); return; }
    if (spinning || !status.available) return;
    setSpinning(true);
    setIdle(false);
    setResult(null);
    try {
      const { data } = await api.post("/spin/claim");
      const target = 360 * 6 + (360 - (data.index * seg + seg / 2));
      setRotation((prev) => prev - (prev % 360) + target);
      setTimeout(() => {
        setSpinning(false);
        setResult(data);
        setTimeout(() => setIdle(true), 2500);
        if (data.amount > 0) { toast.success(data.tier === "jackpot" ? `🏆 JACKPOT! ₹${data.amount} on ${data.game}!` : `🎉 ${data.game} — you won ₹${data.amount}! Added to your wallet.`); refreshWallet(); }
        else toast.info(ta ? "இந்த முறை இல்லை — அடுத்த spin 6 மணி நேரத்தில்!" : "No luck this time — next spin in 6 hours!");
        if (data.streak_bonus) toast.success(ta ? "🔥 3 நாள் streak! ஒரு extra bonus spin ready." : "🔥 3-day streak! One extra bonus spin is ready.", { duration: 8000 });
        setStatus((s) => ({ ...s, ...data, available: data.bonus_spin || false }));
      }, 5200);
    } catch (e) {
      setSpinning(false);
      setIdle(true);
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
  };

  const subtitle = guest
    ? (ta ? "Login பண்ணி இப்போவே free spin — ₹5 முதல் ₹100 வரை wallet-ல!" : "Log in for your free spin — ₹5 up to ₹100 straight into your wallet!")
    : status.bonus_spin
    ? (ta ? "🎁 Bonus spin ready — share பண்ணதுக்கு நன்றி! இப்போவே சுத்துங்க." : "🎁 Bonus spin ready — thanks for sharing! Spin now, no waiting.")
    : status.available
      ? (ta ? `${status.today || 0}/${status.per_day || 4} spins இன்று · ${status.won_today ? "இன்று ஒரு வெற்றி ஆச்சு — try again வரும்" : "இன்று இன்னும் வெற்றி இல்லை — வாய்ப்பு இருக்கு!"}` : `${status.today || 0}/${status.per_day || 4} spins today · ${status.won_today ? "today's prize already won" : "today's prize still open!"}`)
      : status.day_capped ? (ta ? `இன்றைய ${status.per_day || 4} spins முடிஞ்சது — நாளை வாங்க` : `Daily limit ${status.per_day || 4}/${status.per_day || 4} — come back tomorrow`) : (ta ? `அடுத்த spin ${fmtLeft()}-ல` : `Next spin in ${fmtLeft()}`);

  return (
    <div className="rounded-3xl overflow-hidden shadow-xl border border-amber-300/60" style={{ background: "radial-gradient(130% 100% at 50% 0%, #E11D48 0%, #9F1239 45%, #4C0519 100%)" }} data-testid="spin-wheel-card">
      {/* Headline FIRST */}
      <div className="px-4 pt-4 pb-2 text-center">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-[0.25em] uppercase text-amber-200"><Crown className="w-3.5 h-3.5" />ராயல் லக்ஷ்மி · ROYAL LAKSHMI</div>
        <h2 className="font-display font-black text-2xl sm:text-3xl leading-tight mt-1 rl-gold-text" data-testid="spin-headline">{ta ? "🎡 ராயல் Lucky Spin" : "🎡 Royal Lucky Spin"}</h2>
        <p className="text-white text-xs sm:text-sm font-semibold mt-1" data-testid="spin-tagline">{ta ? "ஒவ்வொரு game-க்கும் ஒரு பரிசு — ₹5 முதல் ₹100 JACKPOT வரை · 6 மணிக்கு ஒரு spin" : "A prize on every game — ₹5 up to the ₹100 JACKPOT · one spin every 6 hrs"}</p>
        <div className="text-[11px] text-amber-100 mt-1" data-testid="spin-subtitle">{subtitle}</div>
      </div>

      {/* Wheel */}
      <div className="relative w-72 h-72 mx-auto my-2">
        <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-20 w-0 h-0 drop-shadow" style={{ borderLeft: "11px solid transparent", borderRight: "11px solid transparent", borderTop: "22px solid #FDE68A" }} />
        <div className="absolute inset-0 rounded-full" style={{ boxShadow: "0 0 0 7px #FFD700, 0 0 0 11px #FFF3B0, 0 0 60px rgba(255,215,0,0.75)" }} />
        {Array.from({ length: n }).map((_, i) => (
          <span key={i} className="absolute left-1/2 top-1/2 w-2.5 h-2.5 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)] z-10 animate-pulse" style={{ animationDelay: `${i * 0.12}s`, transform: `translate(-50%,-50%) rotate(${i * seg}deg) translateY(-140px)` }} />
        ))}
        <div className={`w-72 h-72 rounded-full relative overflow-hidden ${idle && !spinning ? "rl-wheel-idle" : ""}`}
          style={{ background: bg, transform: `rotate(${rotation}deg)`, transition: spinning ? "transform 5s cubic-bezier(0.12,0.85,0.2,1)" : "none" }}>
          {segments.map((s, i) => (
            <span key={i} className="absolute left-1/2 top-1/2 flex flex-col items-center justify-center text-white" data-testid={`spin-seg-${i}`}
              style={{ transform: `translate(-50%,-50%) rotate(${i * seg + seg / 2}deg) translateY(-96px) rotate(-90deg)`, width: 84 }}>
              <span className={`font-display font-black leading-none ${s.tier === "jackpot" ? "text-[15px] text-slate-900" : s.amount === 0 ? "text-[8px] tracking-wide text-rose-700" : DARK_SEG(s) ? "text-[13px] text-slate-900" : "text-[13px] text-white"}`} style={DARK_SEG(s) ? undefined : { textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}>{s.tier === "jackpot" ? "★ " : ""}{s.amount === 0 ? "TRY AGAIN" : s.label}</span>
              {s.game ? <span className={`text-[7px] font-bold uppercase tracking-wider leading-none mt-0.5 ${DARK_SEG(s) ? "text-slate-900/80" : "text-white/95"}`} style={DARK_SEG(s) ? undefined : { textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>{s.game}</span> : null}
            </span>
          ))}
          <span className="absolute inset-0 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle at 50% 28%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 45%), radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.18) 100%)" }} />
        </div>
        <button type="button" onClick={spin} disabled={!guest && (spinning || !status.available)} aria-label="Spin the wheel"
          className={`absolute inset-0 m-auto w-20 h-20 rounded-full flex flex-col items-center justify-center z-10 transition-transform border-4 ${(status.available || guest) && !spinning ? "rl-gold-bg border-amber-100 active:scale-90 hover:scale-105 cursor-pointer animate-pulse shadow-[0_0_30px_rgba(253,230,138,0.7)]" : "bg-rose-950 border-amber-300 cursor-default"}`}
          data-testid="spin-center-btn">
          <Crown className={`w-4 h-4 ${(status.available || guest) && !spinning ? "text-slate-900" : "text-amber-400"}`} />
          <span className={`font-display font-black text-xs ${(status.available || guest) && !spinning ? "text-slate-900" : "text-amber-300"}`}>{spinning ? "…" : status.available || guest ? "SPIN" : "WAIT"}</span>
        </button>
      </div>

      <div className="px-4 pb-4">
        <button onClick={spin} disabled={!guest && (spinning || !status.available)}
          className={`w-full font-black py-3 rounded-full mt-1 flex items-center justify-center gap-2 text-sm ${(status.available || guest) && !spinning ? "rl-gold-bg active:scale-[0.99]" : "bg-white/20 text-white"}`}
          data-testid="spin-btn">
          {guest ? <><LogIn className="w-4 h-4" />{ta ? "Login பண்ணி FREE SPIN" : "LOGIN & FREE SPIN"}</> : spinning ? <><Loader2 className="w-4 h-4 animate-spin" />{ta ? "சுத்துது..." : "Spinning..."}</> : status.bonus_spin ? "🎁 BONUS SPIN" : status.available ? (ta ? "இப்போவே SPIN பண்ணுங்க" : "SPIN NOW") : status.day_capped ? (ta ? "நாளை" : "TOMORROW") : (ta ? `அடுத்த spin ${fmtLeft()}` : `Next spin in ${fmtLeft()}`)}
        </button>
        {result && (
          <div className={`text-center text-sm font-black mt-2 ${result.amount > 0 ? "text-emerald-300" : "text-amber-200/80"}`} data-testid="spin-result">
            {result.amount > 0 ? (result.tier === "jackpot" ? `🏆 JACKPOT ₹${result.amount} · ${result.game}!` : `🎉 ${result.game} · ₹${result.amount} ${ta ? "வென்றீர்கள்!" : "won!"}`) : (ta ? "Try again — அடுத்த முறை உங்களுக்குத் தான்!" : "Try again — next one is yours!")}
          </div>
        )}
        {guest && <div className="mt-3 flex flex-wrap justify-center gap-1.5" data-testid="spin-guest-chips">{["₹5", "₹10", "₹25", "₹50", "₹100 JACKPOT"].map((c) => <span key={c} className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${c.includes("100") ? "bg-amber-400 text-slate-900 border-amber-300" : "bg-white/10 text-amber-100 border-white/15"}`}>{c}</span>)}</div>}
      </div>
    </div>
  );
}
